import React from "react";
import { Link } from "react-router-dom";

// This is the component for the Business Strategy blog detail page
const FourthBlog = () => {
  // This would typically be fetched based on URL params in a real app
  const article = {
    id: 5,
    title: "5 Common Proposal Mistakes That Cost You Clients",
    subtitle:
      "Identify and fix critical errors before they impact your bottom line",
    category: "BUSINESS STRATEGY",
    publishDate: "April 25, 2025",
    author: {
      name: "Michael Chen",
      title: "Director of Business Development",
      image: "https://randomuser.me/api/portraits/men/34.jpg",
    },
    featuredImage:
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80",
    readTime: "6 min read",
    content: `
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The High Cost of Proposal Mistakes</h2>
      <p>In today's competitive business environment, a well-crafted proposal can be the difference between landing a lucrative contract or watching it go to a competitor. Yet many organizations continue to make critical mistakes in their proposal development process that sabotage their chances of success.</p>
      
      <p>At SelectProposal, we've analyzed thousands of business proposals across industries, and we consistently see the same errors undermining otherwise qualified companies. In this article, we'll examine the five most common proposal mistakes and provide actionable strategies to address them.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Mistake #1: Generic, Template-Driven Content</h2>
      <p>Perhaps the most prevalent and damaging mistake is relying too heavily on boilerplate content and templates without sufficient customization. When clients receive proposals that feel generic, they immediately sense that you haven't taken the time to understand their specific challenges and needs.</p>
      
      <p>Generic proposals signal to potential clients that you're likely to deliver cookie-cutter solutions rather than tailored strategies that address their unique situation. This perception can be fatal to your chances of winning the business, regardless of your qualifications or capabilities.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Solution: Client-Centered Customization</h2>
      <p>While templates provide valuable structure, each proposal must be substantially customized to the specific client. This means:</p>
      
      <ul>
        <li>Conducting thorough research on the client's industry, challenges, and goals</li>
        <li>Referencing the client's terminology, strategic priorities, and stated objectives</li>
        <li>Tailoring your solution to address their specific pain points</li>
        <li>Including relevant case studies that closely align with their situation</li>
        <li>Personalizing the executive summary to speak directly to key decision-makers' concerns</li>
      </ul>
      
      <p>SelectProposal's AI-powered platform can help identify critical customization opportunities while maintaining efficiency in your proposal process.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Mistake #2: Focusing on Features Instead of Benefits</h2>
      <p>Many proposals fall into the trap of extensively listing product or service features without adequately connecting them to client benefits. This forces the potential client to do the mental work of understanding how your offering will solve their problems or create value for their organization.</p>
      
      <p>Remember that clients don't buy features—they buy outcomes. When your proposal emphasizes technical specifications or service details without explaining their relevance to the client's goals, you miss a critical opportunity to demonstrate value.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Solution: The Features-to-Benefits Bridge</h2>
      <p>For every feature you highlight in your proposal, explicitly connect it to a tangible client benefit using a "which means that..." approach:</p>
      
      <ul>
        <li><strong>Weak:</strong> "Our solution includes automated compliance checking."</li>
        <li><strong>Strong:</strong> "Our solution includes automated compliance checking, which means your team will reduce regulatory risks while saving approximately 15 hours per month currently spent on manual reviews."</li>
      </ul>
      
      <p>This simple restructuring transforms your proposal from a product catalog into a compelling business case that resonates with decision-makers.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Mistake #3: Unclear Pricing Structure</h2>
      <p>Pricing sections that are confusing, incomplete, or lacking transparency can quickly derail an otherwise strong proposal. When clients can't easily understand what they're paying for or suspect hidden costs, trust erodes and purchasing decisions stall.</p>
      
      <p>Common pricing mistakes include:</p>
      
      <ul>
        <li>Burying pricing information deep in the proposal</li>
        <li>Using complex pricing models without clear explanations</li>
        <li>Failing to provide options that accommodate different budget scenarios</li>
        <li>Not clearly connecting price points to value delivered</li>
        <li>Omitting implementation or ongoing maintenance costs</li>
      </ul>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Solution: Transparent Value-Based Pricing</h2>
      <p>Effective proposal pricing should be:</p>
      
      <ul>
        <li>Transparent and comprehensive</li>
        <li>Clearly structured with no hidden costs</li>
        <li>Tied explicitly to value delivery</li>
        <li>Presented with options when appropriate</li>
        <li>Supported by ROI calculations where possible</li>
      </ul>
      
      <p>Consider including a pricing summary table that clearly outlines what's included at each price point, making it easy for clients to understand their investment and expected returns.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Mistake #4: Weak or Missing Social Proof</h2>
      <p>In a business environment where trust is essential, proposals that lack credible social proof struggle to overcome the client's natural risk aversion. Yet many organizations either completely omit evidence of their capabilities or include social proof that lacks specificity and impact.</p>
      
      <p>Without compelling evidence that you've successfully solved similar problems for comparable organizations, clients have little basis for confidence in your claims.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Solution: Strategic Evidence Placement</h2>
      <p>Strengthen your proposal with strategically placed social proof elements including:</p>
      
      <ul>
        <li>Relevant case studies with quantifiable results</li>
        <li>Client testimonials that address specific concerns or objections</li>
        <li>Industry awards and recognition</li>
        <li>Data-driven performance metrics</li>
        <li>Team credentials and subject matter expertise</li>
      </ul>
      
      <p>The most effective social proof is specific, relevant to the client's situation, and placed at strategic points in the proposal where the client might experience doubt or uncertainty.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Mistake #5: No Clear Next Steps or Call to Action</h2>
      <p>Even excellent proposals can fail when they conclude without a clear path forward. Proposals that end abruptly after the pricing section leave the client uncertain about what happens next, creating unnecessary friction in the decision process.</p>
      
      <p>This uncertainty can lead to delays, confusion, or worse—allowing competitors with clearer processes to capture the client's attention in the interim.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">The Solution: A Structured Path Forward</h2>
      <p>Every proposal should include a clear "Next Steps" section that:</p>
      
      <ul>
        <li>Outlines a specific timeline for moving forward</li>
        <li>Provides multiple ways to engage (meeting, demo, pilot, etc.)</li>
        <li>Includes all relevant contact information</li>
        <li>Sets expectations for response times and availability</li>
        <li>Creates a sense of appropriate urgency without being pushy</li>
      </ul>
      
      <p>This section removes ambiguity from the process and makes it easy for interested clients to take immediate action.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Leveraging Technology to Avoid Proposal Pitfalls</h2>
      <p>Modern proposal development tools like SelectProposal can help your organization systematically avoid these common mistakes through:</p>
      
      <ul>
        <li>AI-powered client research and customization recommendations</li>
        <li>Features-to-benefits frameworks and suggestion engines</li>
        <li>Clear pricing templates and ROI calculators</li>
        <li>Integrated case study and testimonial libraries</li>
        <li>Standardized next steps sections with customizable options</li>
      </ul>
      
      <p>These technological solutions not only help prevent critical errors but also significantly accelerate the proposal development process, allowing your team to produce more high-quality proposals in less time.</p>
      
      <h2 style="font-family: Georgia, serif; font-weight: 800; color: #730000; font-size: 1rem;">Conclusion: From Mistakes to Mastery</h2>
      <p>Addressing these five common proposal mistakes can dramatically improve your win rates and accelerate your sales cycle. By focusing on client-centered customization, benefit-oriented messaging, transparent pricing, strategic social proof, and clear next steps, you'll create proposals that stand out from the competition and consistently convert prospects into clients.</p>
      
      <p>Remember that proposal excellence is an ongoing journey rather than a destination. Regular review and refinement of your proposal development process, supported by the right technology and training, will ensure your team continues to produce winning proposals in an evolving business landscape.</p>
    `,
    tags: [
      "Proposal Development",
      "Business Growth",
      "Client Acquisition",
      "Sales Strategy",
      "SelectProposal",
    ],
    relatedPosts: [
      {
        id: 6,
        title: "The Psychology of Persuasive Proposals",
        image:
          "https://images.unsplash.com/photo-1568992687947-868a62a9f521?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8cHN5Y2hvbG9neXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60",
        category: "BUSINESS PSYCHOLOGY",
      },
      {
        id: 7,
        title: "Proposal Design Elements That Win Contracts",
        image:
          "https://images.unsplash.com/photo-1572044162444-ad60f128bdea?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZGVzaWdufGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60",
        category: "DESIGN STRATEGY",
      },
      {
        id: 8,
        title: "How to Build a Proposal That Stands Out",
        image:
          "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8c3RhbmQlMjBvdXR8ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60",
        category: "COMPETITIVE ADVANTAGE",
      },
    ],
  };

  return (
    <div className="font-sans max-w-4xl mx-auto p-4 pb-16">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-pink-500">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/blogs" className="hover:text-pink-500">
          Blogs
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-700">
          {article.title.substring(0, 30)}...
        </span>
      </div>

      {/* Category Tag */}
      <div className="mb-4 text-pink-500 font-bold">{article.category}</div>

      {/* Article Title */}
      <h1 className="text-4xl font-serif font-bold mb-2">{article.title}</h1>
      <h2 className="text-2xl font-serif text-gray-600 mb-6">
        {article.subtitle}
      </h2>

      {/* Article Meta */}
      <div className="flex items-center justify-between mb-8 border-b border-gray-200 pb-4">
        <div className="flex items-center">
          <img
            src={article.author.image}
            alt={article.author.name}
            className="w-12 h-12 rounded-full mr-4 hover:scale-105 transition-transform duration-300"
          />
          <div>
            <div className="font-medium">{article.author.name}</div>
            <div className="text-sm text-gray-500">{article.author.title}</div>
          </div>
        </div>
        <div className="text-sm text-gray-500">
          <span>{article.publishDate}</span>
          <span className="mx-2">•</span>
          <span>{article.readTime}</span>
        </div>
      </div>

      {/* Featured Image with Label */}
      <div className="mb-8 relative">
        <img
          src={article.featuredImage}
          alt={article.title}
          className="w-full h-auto rounded-lg shadow-md hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 right-4 bg-pink-500 text-white p-3 text-sm font-bold">
          <div>Business</div>
          <div>Strategy</div>
        </div>
      </div>

      {/* Article Content */}
      <div
        className="prose prose-lg max-w-none mb-12"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />

      {/* Tags */}
      <div className="mb-12">
        <h3 className="text-lg font-serif font-bold mb-4 text-pink-500">
          Related Topics
        </h3>
        <div className="flex flex-wrap gap-2">
          {article.tags.map((tag, index) => (
            <span
              key={index}
              className="bg-gray-100 cursor-default hover:bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-sm"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      {/* Author Box */}
      <div className="bg-gray-50 p-6 rounded-lg shadow-sm mb-12">
        <div className="flex items-start">
          <img
            src={article.author.image}
            alt={article.author.name}
            className="w-16 h-16 rounded-full mr-6 hover:scale-105 transition-transform duration-300"
          />
          <div>
            <h3 className="font-bold text-lg font-serif mb-2">
              About {article.author.name}
            </h3>
            <p className="text-gray-700 mb-4">
              Michael Chen is a seasoned business development expert with more
              than 12 years of experience helping organizations win competitive
              contracts. As Director of Business Development at SelectProposal,
              he oversees client success strategies and has personally helped
              clients secure over $500 million in contracts. Michael frequently
              conducts workshops on proposal development and is known for his
              practical, results-driven approach to business growth.
            </p>
            <div className="flex gap-4">
              <span className="text-pink-500 cursor-default hover:text-pink-500">
                More articles by this author
              </span>
              <span className="text-pink-500 cursor-default hover:text-pink-500">
                Follow on LinkedIn
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Related Posts */}
      <div>
        <h3 className="text-2xl font-serif font-bold mb-6 cursor-default text-pink-500">
          Related Articles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {article.relatedPosts.map((post) => (
            <span
              key={post.id}
              className="border-b-4 border-red-600 rounded-bl-md rounded-br-md"
            >
              <div className="mb-3">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-48 object-cover rounded-lg hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="mb-1 text-pink-500 font-bold text-sm cursor-default">
                {post.category}
              </div>
              <h4 className="text-lg font-serif font-bold hover:text-[#730000] cursor-default">
                {post.title}
              </h4>
            </span>
          ))}
        </div>
      </div>

      {/* Business Strategy Section */}
      <div className="mt-16">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold font-serif">Business Strategy</h2>
          <span className="text-pink-500 text-sm hover:underline">See all</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Card 1 */}
          <div className="relative border-b-4 border-red-600 rounded-bl-md rounded-br-md">
            <img
              src="https://www.uptonbarn.com/wp-content/uploads/2024/07/Lucy-and-Yousef-High-Resolution-832-of-1006-scaled.jpg"
              alt="Business meeting"
              className="w-full rounded-md h-48 object-cover hover:scale-105 transition-transform duration-300"
            />
            <h3 className="font-serif font-bold mt-2 hover:text-[#730000] cursor-default">
              5 Ways to Improve Your Pitch Meetings
            </h3>
            <p className="text-xs mt-1 cursor-default">
              Master the art of the pitch meeting with these proven techniques
              that will help you convert prospects into clients with confidence.
            </p>
          </div>

          {/* Card 2 */}
          <div className="relative border-b-4 border-red-600 rounded-bl-md rounded-br-md">
            <img
              src="https://images.theconversation.com/files/455174/original/file-20220330-6091-i4h5l2.jpg?ixlib=rb-4.1.0&q=20&auto=format&w=320&fit=clip&dpr=2&usm=12&cs=strip"
              alt="Calendar planning"
              className="w-full rounded-md h-48 object-cover hover:scale-105 transition-transform duration-300"
            />
            <h3 className="font-serif cursor-default font-bold mt-2 hover:text-[#730000]">
              Proposal Planning: The 30-60-90 Day Strategy
            </h3>
            <p className="text-xs mt-1 cursor-default">
              Learn how to create a strategic proposal development timeline that
              ensures quality, consistency, and higher win rates.
            </p>
          </div>

          {/* Card 3 */}
          <div className="relative border-b-4 border-red-600 rounded-bl-md rounded-br-md">
            <img
              src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSq4447GYAE6Wl-B9lmDr7Z-GjFVWXQt7HGaw&s"
              alt="Growth chart"
              className="w-full rounded-md h-48 object-cover hover:scale-105 transition-transform duration-300"
            />
            <h3 className="font-serif cursor-default font-bold mt-2 hover:text-[#730000]">
              Scaling Your Proposal Team: When and How
            </h3>
            <p className="text-xs mt-1 cursor-default">
              Discover the key indicators that it's time to expand your proposal
              team and the best practices for scaling without sacrificing
              quality.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FourthBlog;
